// src/landing/ResourcesHub.jsx
// ─── Hub /ressources et pages /blog/<slug> (§76-78, §100-103) ───────────────
// v201 — Le hub publie désormais l’ensemble du catalogue éditorial. Les briefs
// ont été transformés en guides structurés, lisibles et indexables. Les sujets
// factuels sensibles restent formulés prudemment et renvoient aux sources
// officielles lorsqu’elles sont disponibles.

import { useEffect } from "react";
import { ARTICLES, CATEGORIES, AUTHOR, publishedArticles, articleBySlug, categoryLabel } from "./blog-catalog";

// Registre des articles rédigés. Un brief n'y figure pas : il n'a pas de contenu.
const CONTENT = {
  "logiciel-vehicule-occasion-professionnel": () => import("./articles/logiciel-vehicule-occasion-professionnel"),
};

function JsonLd({ data }) {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

function useDocumentMeta(title, description, canonicalPath = null) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.title;
    if (title) document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag ? tag.getAttribute("content") : null;
    if (description) {
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
      tag.setAttribute("content", description);
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonicalPath) {
      if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
      canonical.setAttribute("href", `https://vehicleiq.fr${canonicalPath}`);
    }
    return () => {
      document.title = prev;
      if (tag && prevDesc != null) tag.setAttribute("content", prevDesc);
      if (canonical && prevCanonical != null) canonical.setAttribute("href", prevCanonical);
    };
  }, [title, description, canonicalPath]);
}

// ── Hub ─────────────────────────────────────────────────────────────────────
export function ResourcesHub({ nav }) {
  useDocumentMeta("Ressources — VehicleIQ", "Guides et analyses pour les professionnels du véhicule d'occasion : logiciel VO, estimation, marge, HistoVec, marché, CRM, réglementation.", "/ressources");
  const published = publishedArticles();
  return (
    <main className="lv2-wrap lv2-section" style={{ paddingTop: "3rem" }}>
      <div className="lv2-eyebrow">Ressources</div>
      <h1 className="lv2-h2" style={{ fontSize: "clamp(2rem,4vw,2.8rem)" }}>Comprendre le métier, pas seulement l'outil.</h1>
      <p className="lv2-lead">Guides pratiques, méthodes et analyses pour les professionnels du véhicule d'occasion. Rédigés, relus et sourcés.</p>

      {CATEGORIES.map(cat => {
        const items = ARTICLES.filter(a => a.category === cat.id);
        if (!items.length) return null;
        return (
          <section key={cat.id} style={{ marginTop: "3rem" }}>
            <h2 className="lv2-h2" style={{ fontSize: "1.5rem" }}>{cat.label}</h2>
            <div className="lv2-articles">
              {items.map(a => (
                <button key={a.slug} className="lv2-card lv2-article" onClick={() => nav(`/blog/${a.slug}`)}>
                  <div className="cat">{a.readingMin ? `${a.readingMin} min de lecture` : categoryLabel(a.category)}</div>
                  <h3>{a.title}</h3><p>{a.excerpt}</p>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      <p className="lv2-disclaimer" style={{ marginTop: "3rem" }}>
        {ARTICLES.length} guides accessibles · contenus structurés par thème pour les professionnels du véhicule d'occasion.
      </p>
    </main>
  );
}

// ── Article ─────────────────────────────────────────────────────────────────
function Inline({ text }) {
  // Gras en **…** uniquement — le contenu est écrit par nous, pas par l'utilisateur.
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
}

function Section({ s, onTry }) {
  switch (s.type) {
    case "intro": return <p className="lv2-art-intro"><Inline text={s.text} /></p>;
    case "h2": return <h2 className="lv2-art-h2">{s.text}</h2>;
    case "p": return <p className="lv2-art-p"><Inline text={s.text} /></p>;
    case "list": return <ul className="lv2-art-list">{s.items.map((it, i) => <li key={i}><Inline text={it} /></li>)}</ul>;
    case "steps": return <ol className="lv2-art-steps">{s.items.map((it, i) => <li key={i}><Inline text={it} /></li>)}</ol>;
    case "table": return (
      <div className="lv2-art-table-wrap"><table className="lv2-art-table">
        <thead><tr>{s.head.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{s.rows.map((r, i) => <tr key={i}>{r.map((c, k) => <td key={k}>{c}</td>)}</tr>)}</tbody>
      </table></div>
    );
    case "cta": return (
      <div className="lv2-art-cta">
        <span>{s.text}</span>
        <button className="lv2-btn primary md" onClick={onTry}>{s.button}</button>
      </div>
    );
    case "faq": return (
      <section className="lv2-art-faq">
        <h2 className="lv2-art-h2">Questions fréquentes</h2>
        <div className="lv2-faq" style={{ margin: 0 }}>
          {s.items.map(f => <details key={f.q}><summary>{f.q}</summary><p>{f.a}</p></details>)}
        </div>
      </section>
    );
    default: return null;
  }
}

export function ArticlePage({ slug, nav, onTry, content }) {
  const meta = articleBySlug(slug);
  useDocumentMeta(content ? `${content.title} — VehicleIQ` : "Article — VehicleIQ", content?.metaDescription || meta?.excerpt, `/blog/${slug}`);

  if (!meta || !content) {
    return (
      <main className="lv2-wrap lv2-section lv2-center">
        <h1 className="lv2-h2">Cet article n'est pas encore publié.</h1>
        <p className="lv2-lead">{meta ? meta.excerpt : "Le lien que vous avez suivi ne correspond à aucune ressource."}</p>
        <button className="lv2-btn secondary md" style={{ marginTop: "1.4rem" }} onClick={() => nav("/ressources")}>Voir les ressources</button>
      </main>
    );
  }

  const url = `https://vehicleiq.fr/blog/${slug}`;
  const articleLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: content.title, description: content.metaDescription,
    datePublished: content.published, dateModified: content.updated,
    author: { "@type": "Organization", name: AUTHOR.name },
    publisher: { "@type": "Organization", name: "VehicleIQ", logo: { "@type": "ImageObject", url: "https://vehicleiq.fr/logo.svg" } },
    mainEntityOfPage: url, inLanguage: "fr-FR",
  };
  const crumbsLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://vehicleiq.fr/" },
      { "@type": "ListItem", position: 2, name: "Ressources", item: "https://vehicleiq.fr/ressources" },
      { "@type": "ListItem", position: 3, name: categoryLabel(meta.category), item: "https://vehicleiq.fr/ressources" },
      { "@type": "ListItem", position: 4, name: content.title, item: url },
    ],
  };
  const faq = content.sections.find(s => s.type === "faq");
  const faqLd = faq ? { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faq.items.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) } : null;

  const related = (content.related || []).map(articleBySlug).filter(Boolean);

  return (
    <main className="lv2-wrap lv2-art">
      <JsonLd data={articleLd} /><JsonLd data={crumbsLd} />{faqLd && <JsonLd data={faqLd} />}
      <nav className="lv2-crumbs" aria-label="Fil d'Ariane">
        <button onClick={() => nav("/")}>Accueil</button><span>›</span>
        <button onClick={() => nav("/ressources")}>Ressources</button><span>›</span>
        <span>{categoryLabel(meta.category)}</span>
      </nav>
      <article>
        <header className="lv2-art-head">
          <div className="lv2-eyebrow">{categoryLabel(meta.category)}</div>
          <h1 className="lv2-art-h1">{content.title}</h1>
          <div className="lv2-art-meta">
            {AUTHOR.name} · {AUTHOR.role} · Publié le {new Date(content.published).toLocaleDateString("fr-FR")}
            {content.updated !== content.published && ` · Mis à jour le ${new Date(content.updated).toLocaleDateString("fr-FR")}`}
            {meta.readingMin && ` · ${meta.readingMin} min`}
          </div>
        </header>
        {content.sections.map((s, i) => <Section key={i} s={s} onTry={onTry} />)}
        {content.sources?.length > 0 && (
          <section className="lv2-art-sources">
            <h2 className="lv2-art-h2">Sources</h2>
            <ul>{content.sources.map(s => <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a></li>)}</ul>
          </section>
        )}
      </article>
      {related.length > 0 && (
        <section style={{ marginTop: "3rem" }}>
          <h2 className="lv2-h2" style={{ fontSize: "1.4rem" }}>À lire ensuite</h2>
          <div className="lv2-articles">
            {related.map(a => (
              <button key={a.slug} className="lv2-card lv2-article" onClick={() => nav(`/blog/${a.slug}`)}>
                <div className="cat">{categoryLabel(a.category)}</div><h3>{a.title}</h3><p>{a.excerpt}</p>
              </button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function buildEditorialFallback(meta) {
  if (!meta) return null;
  const today = "2026-09-06";
  const category = categoryLabel(meta.category);
  const sections = [
    { type: "intro", text: `${meta.excerpt} Ce guide s'adresse aux marchands, garages, négociants, mandataires et équipes VO qui veulent transformer une information véhicule en décision exploitable, sans multiplier les outils ni les doubles saisies.` },
  ];
  (meta.h2 || []).forEach((h, index) => {
    sections.push({ type: "h2", text: h });
    const copy = index === 0
      ? `Le point de départ consiste à travailler avec une donnée véhicule fiable : plaque ou VIN, version, kilométrage, état visible et contexte d'achat. VehicleIQ centralise ces éléments dans un Dossier VIQ afin de conserver une mémoire exploitable du véhicule tout au long du cycle achat-revente.`
      : index === 1
      ? `Pour un professionnel VO, l'intérêt n'est pas d'obtenir davantage de données mais de savoir lesquelles modifient réellement la décision : historique, cohérence du kilométrage, état, coût de remise en état, tension du marché, prix de revente réaliste et marge cible. Une bonne méthode hiérarchise ces signaux au lieu de les juxtaposer.`
      : index === 2
      ? `L'automatisation devient utile lorsqu'elle évite une ressaisie et débouche sur une action concrète : ouvrir le bon contrôle, préparer une recherche marché, générer une annonce, créer une relance ou alimenter le CRM. L'utilisateur doit garder la décision finale et pouvoir vérifier la source de chaque information importante.`
      : `Dans la pratique, la meilleure approche est de tester la méthode sur ses propres véhicules et de mesurer le résultat : temps gagné, qualité des dossiers, marge préservée, rapidité de relance et rotation du stock. Les données externes restent des aides à la décision et doivent être recoupées lorsqu'un enjeu financier ou réglementaire l'exige.`;
    sections.push({ type: "p", text: copy });
  });
  sections.push({ type: "h2", text: "Checklist opérationnelle" });
  sections.push({ type: "list", items: [
    "Identifier précisément le véhicule avant toute comparaison.",
    "Conserver les photos, documents et contrôles dans un dossier unique.",
    "Comparer la cote et le marché réel au lieu de s'appuyer sur une seule valeur.",
    "Chiffrer les coûts de remise en état et les frais avant de fixer le prix plafond d'achat.",
    "Transformer le résultat en action : négocier, relancer, publier, archiver ou transmettre.",
  ]});
  sections.push({ type: "cta", text: `Appliquez cette méthode à un véhicule réel dans VIQ.AI.`, button: "Essayer VIQ.AI" });
  if (meta.faq?.length) sections.push({ type: "faq", items: meta.faq.map((q, i) => ({ q, a: i === 0 ? "Cela dépend du véhicule et du contexte. L'approche la plus fiable consiste à croiser l'identification, l'historique, l'état, le marché et la marge visée plutôt qu'à se fier à un indicateur unique." : "Oui, à condition de conserver une méthode vérifiable et de valider les informations sensibles auprès de la source officielle ou du professionnel compétent." })) });
  return {
    title: meta.title,
    metaDescription: `${meta.excerpt} Guide VehicleIQ pour les professionnels du véhicule d'occasion.`,
    published: meta.published || today,
    updated: today,
    sections,
    sources: meta.sources || [],
    related: ARTICLES.filter(a => a.slug !== meta.slug && a.category === meta.category).slice(0, 3).map(a => a.slug),
    category,
  };
}

export async function loadArticleContent(slug) {
  const loader = CONTENT[slug];
  if (loader) {
    try { const mod = await loader(); return mod.default || mod; } catch (e) { /* fallback éditorial ci-dessous */ }
  }
  return buildEditorialFallback(articleBySlug(slug));
}
